import classNames from 'classnames';
import SchedulingEvent from 'containers/scheduling/scheduling-event/SchedulingEvent/SchedulingEvent';
import { DateTime } from 'luxon';
import moment from 'moment-timezone';
import * as React from 'react';
import { ReactNode } from 'react';
import { Calendar, momentLocalizer, View } from 'react-big-calendar';
import 'react-big-calendar/lib/addons/dragAndDrop/styles.css';
import 'react-big-calendar/lib/addons/dragAndDrop/styles.scss';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import '../Calendar.scss';
import CalendarToolbar from '../CalendarToolbar/CalendarToolbar';
import useCalendarTimeSlots from '../hooks/useCalendarTimeSlots';
import { CalendarView, IDefaultCalendarEvent, OmittedCalendarProps } from '../types/CalendarTypes';
import { eventPropGetter } from '../utils/utils';

const localizer = momentLocalizer(moment);

export interface IBasicCalendarProps extends OmittedCalendarProps<IDefaultCalendarEvent> {
  monthEvent: ReactNode;
  weekEvent: ReactNode;
  dayEvent: ReactNode;
  date: DateTime;
  view: CalendarView;
  events: IDefaultCalendarEvent[];
  onView?: (view: View, time?: DateTime | undefined) => void;
  calendarToolbar: (() => ReactNode) | ReactNode;
  className?: string | object;
  onEventClick?: (event: IDefaultCalendarEvent, mouseEvent: React.SyntheticEvent<HTMLElement>) => void;
  onDrillDown?: (date: Date, view: CalendarView) => void;
}

function BasicCalendar(props: IBasicCalendarProps) {
  const {
    onEventClick,
    monthEvent,
    calendarToolbar,
    weekEvent,
    onView,
    onDrillDown,
    date,
    dayEvent,
    className,
    events,
    view,
    ...calendarProps
  } = props;

  const { timeSlots, steps } = useCalendarTimeSlots();

  function onDrill(date: Date, view: View) {
    if (view === 'month' || view === 'week' || view === 'day') {
      onDrillDown?.(date, view);
    }
  }

  const classes = classNames('calendar', view, className);

  return (
    <Calendar
      className={classes}
      eventPropGetter={eventPropGetter}
      onView={onView}
      popup={true}
      timeslots={timeSlots}
      step={steps}
      date={date.toJSDate()}
      view={view}
      views={{ month: true, day: true }}
      onDrillDown={onDrill}
      components={{
        event: monthEvent as any,
        month: { event: monthEvent as any },
        day: { event: dayEvent as any },
      }}
      events={events}
      onSelectEvent={onEventClick}
      localizer={localizer}
      dayLayoutAlgorithm={'no-overlap'}
      showMultiDayTimes={true}
      tooltipAccessor={() => null as any}
      {...calendarProps}
    />
  );
}

BasicCalendar.defaultProps = {
  monthEvent: SchedulingEvent,
  weekEvent: SchedulingEvent,
  dayEvent: SchedulingEvent,
  calendarToolbar: CalendarToolbar,
};

export default BasicCalendar;
